/**
 * CountryCoverage — pan-Asian country hub navigation.
 *
 * Renders in three variants:
 *   • variant="full"    (default) — used on the Services page
 *   • variant="compact" — used on the Home page
 *   • variant="inline"  — used inside Pillar Guides ("Explore by country")
 *
 * Deliberately visual-light (no thumbnails, no marketing gradients) to
 * preserve the premium/quiet feel the product owner approved.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { trackCtaClick } from "@/lib/ga";
import { COUNTRY_HUBS, CHINA_COVERAGE } from "@/lib/countryHubs";

const CTA_SLUG_PREFIX = {
  full: "services-country-hub",
  compact: "home-country-hub",
  inline: "guide-country-hub",
};

export default function CountryCoverage({
  variant = "full",
  source = "services",
  eyebrow = "Country coverage",
  title = "Pan-Asian sourcing across six manufacturing markets",
  body = "Our execution model runs across the six manufacturing markets where B2B buyers most frequently source. Each country hub captures the local landscape — supplier realities, factory verification, customs, and trade-agreement exposure — so procurement teams can compare and structure programs with confidence.",
  showChina = true,
  showIntro = true,
  className = "",
}) {
  const items = showChina ? [...COUNTRY_HUBS, CHINA_COVERAGE] : COUNTRY_HUBS;
  const ctaPrefix = CTA_SLUG_PREFIX[variant] || "country-hub";
  const testIdRoot = `${source}-country-coverage`;

  // ── COMPACT (Home) ──────────────────────────────────────────────
  if (variant === "compact") {
    return (
      <section
        className={`bg-white border-b border-[#ececef] ${className}`}
        data-testid={testIdRoot}
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="max-w-3xl">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
                {eyebrow}
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-5 text-[28px] sm:text-[36px] md:text-[44px] font-semibold tracking-[-0.03em] text-[#0a0a0a] leading-[1.1] text-balance">
                Sourcing across six manufacturing markets
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-4 text-[15px] leading-relaxed text-[#424245] max-w-xl text-pretty">
                Each country hub captures the local supplier landscape, factory verification realities, customs and trade-agreement exposure — so procurement teams can compare markets before committing.
              </p>
            </Reveal>
          </div>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-0 border-t border-[#ececef]">
            {items.map((c) => {
              const href = c.hubSlug ? `/blog/${c.hubSlug}` : null;
              const Element = href ? Link : "div";
              const props = href
                ? {
                    to: href,
                    onClick: () =>
                      trackCtaClick(`Country Hub: ${c.label}`, `${ctaPrefix}:${c.code}`, {
                        country: c.code,
                      }),
                    className:
                      "group flex items-baseline justify-between gap-4 py-5 border-b border-[#ececef] transition-colors hover:bg-[#fafafa]",
                  }
                : {
                    className:
                      "flex items-baseline justify-between gap-4 py-5 border-b border-[#ececef] opacity-70",
                  };
              return (
                <li key={c.code} className="list-none">
                  <Element {...props} data-testid={`${testIdRoot}-link-${c.code}`}>
                    <div>
                      <div className="text-[16px] tracking-[-0.01em] text-[#0a0a0a] font-medium">
                        {c.label}
                      </div>
                      <p className="mt-1 text-[12.5px] leading-snug text-[#6b6b6b] max-w-xs">
                        {c.lead}
                      </p>
                    </div>
                    {href ? (
                      <ArrowUpRight
                        size={14}
                        className="shrink-0 text-[#424245] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        aria-hidden
                      />
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#8a8a8a]">
                        Cross-service
                      </span>
                    )}
                  </Element>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    );
  }

  // ── INLINE (Guide article) ──────────────────────────────────────
  if (variant === "inline") {
    return (
      <section
        className={`my-14 border-t border-neutral-200 pt-10 ${className}`}
        data-testid={testIdRoot}
      >
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
          Explore by country
        </p>
        <h3 className="mt-3 text-[20px] md:text-[22px] tracking-[-0.01em] text-neutral-900 font-medium text-balance">
          How this service runs across six pan-Asian manufacturing markets
        </h3>
        <p className="mt-3 text-[14px] leading-relaxed text-[#6b6b6b] max-w-2xl">
          Each hub covers the country's supplier landscape, factory verification realities, customs, and trade-agreement exposure specific to that jurisdiction.
        </p>
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-0 border-t border-neutral-200">
          {items.map((c) => {
            const href = c.hubSlug ? `/blog/${c.hubSlug}` : null;
            const Element = href ? Link : "div";
            const props = href
              ? {
                  to: href,
                  onClick: () =>
                    trackCtaClick(`Country Hub: ${c.label}`, `${ctaPrefix}:${c.code}`, {
                      country: c.code,
                    }),
                  className:
                    "group flex items-center justify-between gap-3 py-3.5 border-b border-neutral-200 transition-colors hover:bg-neutral-50",
                }
              : {
                  className:
                    "flex items-center justify-between gap-3 py-3.5 border-b border-neutral-200 opacity-70",
                };
            return (
              <li key={c.code} className="list-none">
                <Element {...props} data-testid={`${testIdRoot}-link-${c.code}`}>
                  <span className="text-[14px] tracking-[-0.005em] text-neutral-900 font-medium">
                    {c.label}
                  </span>
                  {href ? (
                    <ArrowUpRight size={13} className="shrink-0 text-neutral-500 group-hover:text-neutral-900 transition-colors" aria-hidden />
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.14em] text-neutral-400">Cross-service</span>
                  )}
                </Element>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  // ── FULL (Services page) ────────────────────────────────────────
  return (
    <section
      className={`bg-[#f6f6f4] border-t border-[#e6e6e0] ${className}`}
      data-testid={testIdRoot}
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          {showIntro && (
            <div className="md:col-span-4">
              <Reveal>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#424245]">
                  {eyebrow}
                </p>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="mt-4 text-[28px] md:text-[36px] leading-[1.15] tracking-[-0.02em] text-[#0b0b0b] text-balance">
                  {title}
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="mt-5 text-[15px] leading-relaxed text-[#424245] max-w-md text-pretty">
                  {body}
                </p>
              </Reveal>
            </div>
          )}

          <div className={showIntro ? "md:col-span-8" : "md:col-span-12"}>
            <ul className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[#e6e6e0] border-t border-b border-[#e6e6e0]">
              {items.map((c) => {
                const href = c.hubSlug ? `/blog/${c.hubSlug}` : null;
                const Element = href ? Link : "div";
                const props = href
                  ? {
                      to: href,
                      onClick: () =>
                        trackCtaClick(`Country Hub: ${c.label}`, `${ctaPrefix}:${c.code}`, {
                          country: c.code,
                        }),
                      className:
                        "group flex items-baseline justify-between gap-6 px-2 sm:px-6 py-6 transition-colors hover:bg-white",
                    }
                  : {
                      className:
                        "flex items-baseline justify-between gap-6 px-2 sm:px-6 py-6 opacity-70",
                    };
                return (
                  <li key={c.code} className="list-none">
                    <Element {...props} data-testid={`country-hub-link-${c.code}`}>
                      <div>
                        <div className="text-[18px] tracking-[-0.01em] text-[#0b0b0b] font-medium">
                          {c.label}
                        </div>
                        <p className="mt-1.5 text-[13px] leading-snug text-[#6b6b6b] max-w-xs">
                          {c.lead}
                        </p>
                      </div>
                      {href ? (
                        <ArrowUpRight
                          size={16}
                          className="shrink-0 text-[#424245] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          aria-hidden
                        />
                      ) : (
                        <span className="text-[11px] uppercase tracking-[0.15em] text-[#8a8a8a]">
                          Covered across services
                        </span>
                      )}
                    </Element>
                  </li>
                );
              })}
            </ul>
            <p className="mt-6 text-[12px] text-[#6b6b6b] max-w-lg">
              China is represented across every service page and comparative article; hub-page is not required.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
